/* HealthspanX Luminous Field V2 homepage / video + accessible motion, 15 Oct 2026.
   Content and navigation are always present without JS. No analytics / commerce interactions. */
(() => {
  'use strict';
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = () => !!(navigator.connection && navigator.connection.saveData);
  const motionAllowed = () => root.dataset.hsxMotion !== 'false' && !reduceMotion.matches;
  const clamp = (n,lo,hi) => Math.max(lo,Math.min(hi,n));
  const loaded = new WeakSet();
  const heroInitialized = new WeakSet();
  const sceneInitialized = new WeakSet();
  const revealInitialized = new WeakSet();
  const orbited = new WeakSet();

  const setupHeroVideo = (scope) => {
    scope.querySelectorAll('video.hsx-v2-hero__video').forEach(video => {
      if (heroInitialized.has(video)) return;
      heroInitialized.add(video);
      const stage = video.closest('.hsx-v2-hero__visual');
      if (!stage) return;
      let visible = true;
      const mobileViewport = window.matchMedia('(max-width: 48rem)');
      const viewportSource = () => {
        const mobile = mobileViewport.matches;
        const source = mobile
          ? video.dataset.hsxMobileVideo || video.dataset.hsxDesktopVideo
          : video.dataset.hsxDesktopVideo || video.dataset.hsxMobileVideo;
        const poster = mobile
          ? video.dataset.hsxMobilePoster || video.dataset.hsxDesktopPoster
          : video.dataset.hsxDesktopPoster || video.dataset.hsxMobilePoster;
        if (poster && video.getAttribute('poster') !== poster) video.setAttribute('poster', poster);
        return source;
      };
      const pause = () => video.pause();
      const start = () => {
        const source = viewportSource();
        if (!motionAllowed() || saveData() || !visible || document.hidden) return pause();
        if (source && video.getAttribute('src') !== source) {
          video.src = source;
          video.load();
        }
        video.muted = true;
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      };
      viewportSource();
      mobileViewport.addEventListener?.('change', start);
      if ('IntersectionObserver' in window) {
        const watch = new IntersectionObserver(entries => {
          visible = entries.some(e => e.isIntersecting && e.intersectionRatio >= .12);
          if (visible) start(); else pause();
        },{threshold:[0,.12,.3]});
        watch.observe(stage);
      } else start();
      document.addEventListener('visibilitychange',() => document.hidden ? pause() : start());
      reduceMotion.addEventListener?.('change',() => reduceMotion.matches ? pause() : start());
    });
  };

  const reveal = scope => {
    if (!('IntersectionObserver' in window) || !motionAllowed()) return;
    const selectors = [
      '.hsx-v2-home-heading','.hsx-v2-home-lead','.hsx-v2-home-rail__item',
      '.hsx-v2-home-product__detail','.hsx-v2-home-quality__card',
      '.hsx-v2-home-evidence__card','.hsx-v2-home-tools__card',
      '.hsx-v2-home-journal__card','.hsx-v2-home-faq__item',
      '.hsx-v2-home-close__visual'
    ].join(',');
    const items=[...scope.querySelectorAll(selectors)].filter(x=>!revealInitialized.has(x));
    if (!items.length) return;
    const observer=new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el=entry.target;
        observer.unobserve(el);
        if (revealInitialized.has(el)) return;
        revealInitialized.add(el);
        const row = el.parentElement;
        const same = row ? [...row.children].filter(x=>x.matches?.(selectors)) : [];
        const i = same.indexOf(el);
        const delay=i<0?0:(i%4)*85;
        if (typeof el.animate === 'function') {
          el.animate([{opacity:.58,transform:'translateY(16px)'},{opacity:1,transform:'translateY(0)'}],{
            duration:800,delay,easing:'cubic-bezier(.22,.61,.36,1)',fill:'none'
          });
        }
      });
    },{threshold:0.1,rootMargin:'0px 0px -6% 0px'});
    items.forEach(el=>observer.observe(el));
  };

  const science = scope => {
    scope.querySelectorAll('[data-hsx-scene-section]').forEach(section => {
      if (sceneInitialized.has(section)) return;
      sceneInitialized.add(section);
      const stage=section.querySelector('[data-hsx-scene]');
      if (!stage) return;
      let active=true,pending=false;
      const reset=() => {
        ['--hsx-scene-x','--hsx-scene-y','--hsx-scene-scale','--hsx-scene-cap',
         '--hsx-scene-cell','--hsx-scene-line','--hsx-scene-gloss'].forEach(key=>stage.style.removeProperty(key));
      };
      const update=() => {
        pending=false;
        if (!motionAllowed() || window.matchMedia('(max-width: 64rem)').matches) {reset();return;}
        const rect=section.getBoundingClientRect();
        const distance=Math.max(1,rect.height-window.innerHeight);
        const p=clamp(-rect.top/distance,0,1);
        stage.style.setProperty('--hsx-scene-x',(32*(p-1)).toFixed(2)+'%');
        stage.style.setProperty('--hsx-scene-y',(15*(1-p)).toFixed(2)+'%');
        stage.style.setProperty('--hsx-scene-scale',(.58+.42*p).toFixed(3));
        stage.style.setProperty('--hsx-scene-cap',(-18*clamp((p-.48)*2,0,1)).toFixed(2)+'%');
        stage.style.setProperty('--hsx-scene-cell',(1-.82*p).toFixed(3));
        stage.style.setProperty('--hsx-scene-line',clamp((p-.48)*2.3,0,1).toFixed(3));
        stage.style.setProperty('--hsx-scene-gloss',clamp((p-.52)*2.3,0,.8).toFixed(3));
      };
      const tick=()=>{if(!pending){pending=true;requestAnimationFrame(update)}};
      if('IntersectionObserver' in window){
        const io=new IntersectionObserver(entries=>{active=entries[0]?.isIntersecting??false;if(active)tick()},{rootMargin:'12% 0px 12% 0px'});
        io.observe(section);
      }
      window.addEventListener('scroll',()=>{if(active)tick()},{passive:true});
      window.addEventListener('resize',tick,{passive:true});
      reduceMotion.addEventListener?.('change',tick);
      update();
    });
  };

  const orbitCounter = scope => {
    scope.querySelectorAll('[data-hsx-count-to]').forEach(el=>{
      if (orbited.has(el)) return;
      orbited.add(el);
      const goal=parseInt(el.dataset.hsxCountTo,10);
      if (!Number.isSafeInteger(goal) || goal<0 || goal>1000 || !motionAllowed() || !('IntersectionObserver' in window)) return;
      const io=new IntersectionObserver(entries=>{
        if(!entries[0]?.isIntersecting)return;
        io.disconnect();
        const begin=performance.now();
        const frame=now=>{
          if(!el.isConnected)return;
          if(!motionAllowed()){el.textContent=String(goal);return;}
          const p=clamp((now-begin)/1100,0,1);
          const ease=1-Math.pow(1-p,3);
          el.textContent=String(Math.round(goal*ease));
          if(p<1)requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      },{threshold:.6});
      io.observe(el);
    });
  };

  const init=scope=>{
    if(!scope) return;
    setupHeroVideo(scope);
    science(scope);
  };
  document.addEventListener('shopify:section:load',event=>init(event.target));
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document),{once:true});
  else init(document);
})();


